<template>
  <div class="collections">
    <HeadSection title="Collections" permissionOfSection="unified-collection">
      <template #actions>
        <a-col :span="14" class="text-right">
          <div>
            <a-button type="primary" size="large"
              @click="isCreateModalOpen = true" :disabled="!createPermission">
              <a-icon type="plus" /> New collection
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="unified-collection">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <div style="flex-grow: 1">
          <a-input-search v-model="search" placeholder="Input search text" enter-button @change="getCollections" :loading="loading" />
        </div>
      </div>
      <a-table :columns="columns" :data-source="collections" :rowKey="(record, index) => index" :loading="loading"
        :pagination="isPaginationAvailable" @change="changePagination"
        class="collections__table">
        <template slot="created_at" slot-scope="text, record">
          <span>{{ formatDate(record.created_at) }}</span>
        </template>
        <template slot="actions" slot-scope="text, record">
          <div class="row-operations d-flex justify-content-between">
            <a-switch :default-checked="!!record.is_active" :disabled="!editPermission" @change="handleActiveSwitch(record.id)" />
            <a class="font-size-18 edit-icon text-primary" :class="{ 'text-gray-5 icon-disabled':  !editPermission }" :disabled="!editPermission" @click="openCollectionEdit(record.id, record.name)">
              <a-icon type="edit" />
            </a>
            <a-popconfirm placement="left" :disabled="!editPermission" title="Are you sure you want to delete this collection?"
              @confirm="() => deleteCollection(record.id)">
              <a-icon class="font-size-18 text-danger delete-icon" :class="{ 'text-gray-5 icon-disabled': !editPermission }" style="margin-top: 2px;" type="delete" />
            </a-popconfirm>
          </div>
        </template>
      </a-table>
    </MainContainer>
    <a-modal v-model="isCreateModalOpen" title="New collection" :footer="null" width="450px" :destroyOnClose="true">
      <CollectionModal @closeCollectionModal="isCreateModalOpen = false" mode="create" @addNewCollection="handleAddCollection" />
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'

import CollectionsService from '@/services/api/apiCollectionsService'
import CollectionModal from '../modals/collectionModal.vue'

export default {
  name: 'CollectionsList',
  components: {
    HeadSection,
    MainContainer,
    CollectionModal,
  },
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCreateModalOpen: false,
      loading: false,
      collections: null,
      search: '',
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          width: '10%',
          scopedSlots: { customRender: 'id' },
        },
        {
          title: 'Name',
          dataIndex: 'name',
          width: '27%',
        },
        {
          title: 'Created',
          dataIndex: 'created_at',
          width: '15%',
          scopedSlots: { customRender: 'created_at' },
        },
        {
          title: 'Items',
          dataIndex: 'items_count',
          width: '10%',
          scopedSlots: { customRender: 'items_count' },
        },
        {
          title: 'Actions',
          width: '5%',
          scopedSlots: { customRender: 'actions' },
        },
      ],
      pagination: {
        pageSize: 15,
        current: 1,
        total: 0,
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isPaginationAvailable() {
      return (this.collections && this.collections.length >= 15) || this.pagination.current !== 1 ? this.pagination : false
    },
  },
  async created() {
    await this.getCollections()
  },
  methods: {
    formatDate: function (date) {
      return this.$moment(date).tz('Etc/GMT-3').format('DD.MM.YY HH:mm')
    },
    changePagination(pagination) {
      this.pagination = pagination
      this.getCollections()
    },
    openCollectionEdit(collectionId, collectionName) {
      const encodedName = encodeURIComponent(collectionName)
      this.$router.push(`/profiles/collections/items/${collectionId}/${encodedName}`)
    },
    handleAddCollection(newCollection) {
      this.isCreateModalOpen = false
      this.collections.unshift(newCollection)
    },
    async getCollections() {
      try {
        this.loading = true
        const collectionsResponse = await CollectionsService.getCollections(this.pagination.current, this.pagination.pageSize, this.search)
        this.collections = collectionsResponse.data.data
        this.pagination.total = collectionsResponse.data.meta.total
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
        this.loading = false
      } finally {
        this.loading = false
      }
    },
    async handleActiveSwitch(collectionId) {
      try {
        const collectionActivityResponse = await CollectionsService.toggleCollectionActivity(collectionId)

        if (collectionActivityResponse.data) {
          this.$notification.success({
            message: 'Active status changed successfully',
          })
        }
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    async deleteCollection(collectionId) {
      try {
        const collectionDeleteResponse = await CollectionsService.deleteCollection(collectionId)

        if (collectionDeleteResponse.data) {
          const deletedIndex = this.collections.findIndex(collection => collection.id === collectionId)
          if (deletedIndex !== -1) {
            this.collections.splice(deletedIndex, 1)
          }
          this.$notification.success({
            message: 'The collection was successfully deleted',
          })
        }
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      }
    },
  },
}
</script>
